'use client';

import { useTransparencyBackdrop } from '@prezly/content-renderer-react-js';
import { UploadcareImage } from '@prezly/uploadcare';
import LegacyImage, { type UploadcareImageDetails } from '@prezly/uploadcare-image';
import classNames from 'classnames';
import { useCallback, useMemo, useRef } from 'react';

interface Props {
    imageDetails: UploadcareImageDetails;
    className?: string;
}

export function GalleryCardThumbnail({ imageDetails, className }: Props) {
    const imageRef = useRef<HTMLImageElement | null>(null);
    const attachImageRef = useCallback((anchor: HTMLSpanElement | null) => {
        imageRef.current = anchor?.querySelector('img') ?? null;
    }, []);
    const image = useMemo(
        () =>
            new UploadcareImage({
                uuid: imageDetails.uuid,
                filename: imageDetails.filename,
                mimeType: imageDetails.mime_type,
                size: imageDetails.size,
                originalWidth: imageDetails.original_width,
                originalHeight: imageDetails.original_height,
                effects: imageDetails.effects,
            }),
        [imageDetails],
    );
    const hasTransparencyBackdrop = useTransparencyBackdrop(image, imageRef);

    // The legacy picture component does not forward refs; observe its actual image.
    return (
        <span ref={attachImageRef} style={{ display: 'contents' }}>
            <LegacyImage
                imageDetails={imageDetails}
                lazy
                layout="fill"
                objectFit="cover"
                className={classNames(className, {
                    'prezly-slate-media--transparency-backdrop': hasTransparencyBackdrop,
                })}
            />
        </span>
    );
}
